import "../../moneyReports.css"
import { useState, useEffect, useContext } from "react"
import AuthContext from "contexte/AuthContext"
import { useTranslation } from "react-i18next"
import DashboardLayout from "components/layout/dashboard/DashboardLayout"
import FilterComponent from "components/Filters/FilterComponent/FilterComponent"
import PageLoader from "components/pageLoader/PageLoader"
import Tableau from "components/DataComponents/Tableau/Tableau"
import { fetchExpensesByDay } from "services/fetchMoneyReports"
import { RELEASE_DATE } from 'utils/date';
import { Amount, EmptyState, ErrorState, Pager, ReportHeader, SummaryTiles, formatEur } from "../../MoneyReportParts"

const COLUMNS = [
    { key: 'fx', label: 'Frais de change' },
    { key: 'lmax', label: 'Frais LMAX' },
    { key: 'exante', label: 'Frais Exante' },
    { key: 'cards', label: 'Stripe' },
    { key: 'partner', label: 'Affiliation' },
    { key: 'sales', label: 'Sales' },
    { key: 'gifts', label: 'Cadeaux' },
    { key: 'gesture', label: 'Gestes commerciaux' },
    { key: 'overnight', label: 'Overnight' },
]

const Expenses = () => {
    const { user } = useContext(AuthContext);
    const { t } = useTranslation();

    const [days, setDays] = useState([]);
    const [totals, setTotals] = useState(null);
    const [rowsCount, setRowsCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [selectedDate, setSelectedDate] = useState({
        date1: { startDate: new Date(RELEASE_DATE), endDate: new Date() },
        date2: { startDate: null, endDate: null },
    });
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(0);

    const fetchData = async () => {
        try {
            setError(false);
            const response = await fetchExpensesByDay({ user, limit, page, startDate: selectedDate.date1.startDate, endDate: selectedDate.date1.endDate });
            // la ligne Total (WITH ROLLUP) n'arrive qu'en tête de la première page
            const totalRow = response.find(row => row.index_type === 0);
            if (totalRow) setTotals(totalRow);
            else if (page === 0) setTotals(null);
            setDays(response.filter(row => row.index_type !== 0));
            setRowsCount(response.length);
        } catch (e) {
            console.log(e);
            setError(true);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (selectedDate.date1.startDate !== null && selectedDate.date1.endDate !== null) {
            fetchData();
        }
    }, [selectedDate, page, limit]);

    const handleDate = (newDate) => {
        setSelectedDate(newDate);
        setPage(0);
    }

    const handleLimit = (newLimit) => {
        setLimit(newLimit);
        setPage(0);
    }

    const tbody = days.map(item => ({
        data: [
            <span className="money_report_num money_report_date">{item.date_formatted}</span>,
            <Amount value={item.total} strong />,
            ...COLUMNS.map(column => <Amount value={item[column.key]} />)
        ]
    }));

    // tous les champs de la ligne Total (sauf date_formatted et index_type), dans l'ordre des colonnes du tableau
    const columnIndex = (key) => {
        const index = COLUMNS.findIndex(column => column.key === key);
        return index === -1 ? COLUMNS.length : index;
    }
    const tiles = totals ? [
        { label: 'Total sur la période', value: formatEur(totals.total), main: true },
        ...Object.keys(totals)
            .filter(key => !['date_formatted', 'index_type', 'total'].includes(key))
            .sort((a, b) => columnIndex(a) - columnIndex(b))
            .map(key => ({ label: COLUMNS.find(column => column.key === key)?.label ?? key, value: formatEur(totals[key]) }))
    ] : [];

    return (
        <DashboardLayout page={t("pages_name.report_expenses")}>
            <div className="money_report_page">
                <ReportHeader
                    title="Dépenses"
                    hint="Coûts supportés par Goliaths, par jour : frais de change, frais des brokers (LMAX, Exante), Stripe, affiliation, sales, cadeaux, gestes commerciaux et overnight."
                />

                <div className="money_report_toolbar">
                    <FilterComponent
                        date={true}
                        limit={true}
                        fetchLimit={limit}
                        setFetchLimit={handleLimit}
                        selectedDate={selectedDate}
                        setSelectedDate={handleDate}
                    />
                </div>

                {
                    loading ? (
                        <div className='pageLoaderBox'>
                            <PageLoader />
                        </div>
                    ) : error ? (
                        <ErrorState title="Erreur lors du chargement des dépenses" onRetry={fetchData} />
                    ) : (
                        <>
                            {totals && <SummaryTiles tiles={tiles} />}
                            <div className="money_report_card">
                                {
                                    tbody.length || page > 0 ? (
                                        <>
                                            <Tableau
                                                thead={['Date', 'Total', ...COLUMNS.map(column => column.label)]}
                                                tbody={tbody}
                                            />
                                            <Pager page={page} setPage={setPage} hasNextPage={rowsCount >= limit} />
                                        </>
                                    ) : (
                                        <EmptyState title="Aucune dépense sur la période" hint="Élargissez les dates pour voir les dépenses." />
                                    )
                                }
                            </div>
                        </>
                    )
                }
            </div>
        </DashboardLayout>
    )
}

export default Expenses
